import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Row, Col, Card, Button, Form, Badge, InputGroup, Table, Modal,
  ProgressBar, Alert,
} from "react-bootstrap";
import {
  Search, Edit2, Trash2, MessageCircle, Download, Star, Package,
  CheckCircle, Clock, Truck, XCircle, FileText, QrCode, Shield,
  Droplet, Pill, UtensilsCrossed,
} from "lucide-react";
import "../../assets/css/receiverdashboard/MyListings.css";
import ReceiverMasterLayout from "../../components/layout/ReceiverMasterLayout";
import AppContext from "../../context/AppContext";

function MyListings() {
  const { baseUrl } = useContext(AppContext);

  const [myListings, setMyListings]             = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [searchFilter, setSearchFilter]         = useState("");
  const [categoryFilter, setCategoryFilter]     = useState("all");
  const [statusFilter, setStatusFilter]         = useState("all");
  const [showEditModal, setShowEditModal]        = useState(false);
  const [showDeleteModal, setShowDeleteModal]    = useState(false);
  const [showProofModal, setShowProofModal]      = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedListing, setSelectedListing]   = useState(null);
  const [rating, setRating]                     = useState(0);
  const [otpCode, setOtpCode]                   = useState("");
  const [showNotification, setShowNotification] = useState(false);

  // ── helper: extract array from any response shape ──
  const extractArray = (data, keys) => {
    if (Array.isArray(data)) return data;
    for (const key of keys) {
      if (Array.isArray(data?.[key])) return data[key];
    }
    return [];
  };

  // ========================= FETCH ALL DATA =========================
  useEffect(() => {
    if (!baseUrl) return;
    fetchListings();
  }, [baseUrl]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const [foodRes, bloodRes, medicineRes] = await Promise.allSettled([
        axios.get(`${baseUrl}/donor/dashboard/viewAllDonation`),
        axios.get(`${baseUrl}/receiver/blood/viewAllRequests`),
        axios.get(`${baseUrl}/donor/dashboard/viewAllMedicine`),
      ]);

      // ── FOOD ── Route: GET /donor/dashboard/viewAllDonation
      // Response shape: { success: true, donation: [...] }
      let foodData = [];
      if (foodRes.status === "fulfilled") {
        const list = extractArray(foodRes.value.data, ["donation", "donations", "data", "food"]);
        foodData = list.map((item) => ({
          id:            item.id,
          category:      "Food",
          itemName:      item.food_item_name   || "Food Item",
          quantity:      `${item.quantity || ""} ${item.unit || ""}`.trim(),
          extraInfo:     `Serves: ${item.approx_people_served || "N/A"} people`,
          urgency:       item.available_time   || "—",
          status:        item.status           || "pending",
          hospital:      "—",
          city:          item.city             || "—",
          pickupDate:    item.pickup_date      || "—",
          pickupAddress: item.pickup_address   || "—",
          contact:       item.contact_number   || "—",
          donorName:     "—",
          description:   item.food_description || "—",
          isFresh:       item.is_freshly_prepared == 1,
          bloodGroup:    "—",
          relation:      "—",
          expiryDate:    "—",
        }));
      }

      // ── BLOOD ── Route: GET /receiver/blood/viewAllRequests
      // Response shape: { success: true, requests: [...] }
      let bloodData = [];
      if (bloodRes.status === "fulfilled") {
        const list = extractArray(bloodRes.value.data, ["requests", "request", "data", "blood"]);
        bloodData = list.map((item) => ({
          id:            item.id,
          category:      "Blood",
          itemName:      `${item.blood_group || ""} Blood`.trim(),
          quantity:      `${item.units || "—"} Units`,
          extraInfo:     `Patient: ${item.patient_name || "—"}`,
          urgency:       item.urgency       || "—",
          status:        item.status        || "pending",
          hospital:      item.hospital      || "—",
          city:          item.city          || "—",
          pickupDate:    "—",
          pickupAddress: "—",
          contact:       item.phone         || "—",
          donorName:     "—",
          description:   "—",
          isFresh:       false,
          bloodGroup:    item.blood_group   || "—",
          relation:      item.relation      || "—",
          expiryDate:    "—",
        }));
      }

      // ── MEDICINE ── Route: GET /donor/dashboard/viewAllMedicine
      // Response shape: { success: true, donations: [...] }
      let medicineData = [];
      if (medicineRes.status === "fulfilled") {
        const list = extractArray(medicineRes.value.data, ["donations", "donation", "medicines", "data"]);
        medicineData = list.map((item) => ({
          id:            item.id,
          category:      "Medicine",
          itemName:      item.medicine_name  || "Medicine",
          quantity:      `${item.quantity || ""} ${item.quantity_unit || ""}`.trim(),
          extraInfo:     `Donor: ${item.donor_name || "—"}`,
          urgency:       item.available_time || "—",
          status:        item.donation       || item.status || "pending",
          hospital:      "—",
          city:          item.city           || "—",
          pickupDate:    item.pickup_date    || "—",
          pickupAddress: item.pickup_address || "—",
          contact:       item.contact_number || "—",
          donorName:     item.donor_name     || "—",
          description:   item.description   || "—",
          isFresh:       item.is_unopened == 1,
          bloodGroup:    "—",
          relation:      "—",
          expiryDate:    item.expiry_date    || "—",
        }));
      }

      const combined = [...foodData, ...bloodData, ...medicineData];
      console.log(`MyListings → Food:${foodData.length} | Blood:${bloodData.length} | Medicine:${medicineData.length} | Total:${combined.length}`);
      setMyListings(combined);
    } catch (err) {
      console.error("MyListings fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ========================= FILTERS =========================
  const filteredListings = myListings.filter((l) => {
    const q = searchFilter.toLowerCase();
    const matchesSearch =
      String(l.itemName).toLowerCase().includes(q) ||
      String(l.id).toLowerCase().includes(q) ||
      String(l.city).toLowerCase().includes(q);
    const matchesCategory = categoryFilter === "all" || l.category === categoryFilter;
    const matchesStatus   = statusFilter   === "all" || l.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // ========================= HELPERS =========================
  const getCategoryIcon = (cat) => {
    if (cat === "Blood")    return <Droplet size={13} />;
    if (cat === "Medicine") return <Pill size={13} />;
    return <UtensilsCrossed size={13} />;
  };

  const getCategoryColor = (cat) => {
    const map = { Food: "success", Blood: "danger", Medicine: "info" };
    return map[cat] || "secondary";
  };

  const normalizeStatus = (s) => {
    const map = {
      pending:   "Pending",
      approved:  "Approved",
      verified:  "Approved",
      shipped:   "Shipped",
      picked_up: "Shipped",
      received:  "Received",
      delivered: "Received",
      rejected:  "Cancelled",
      cancelled: "Cancelled",
    };
    return map[(s || "").toLowerCase()] || "Pending";
  };

  const getStatusVariant = (status) => {
    const variants = {
      Pending: "warning", Approved: "info", Shipped: "primary",
      Received: "success", Cancelled: "danger",
    };
    return variants[normalizeStatus(status)] || "secondary";
  };

  const getStatusIcon = (status) => {
    const n = normalizeStatus(status);
    const icons = {
      Pending:   <Clock size={13} />,
      Approved:  <CheckCircle size={13} />,
      Shipped:   <Truck size={13} />,
      Received:  <Package size={13} />,
      Cancelled: <XCircle size={13} />,
    };
    return icons[n] || <Clock size={13} />;
  };

  const getProgress = (status) => {
    const map = { Pending: 25, Approved: 50, Shipped: 75, Received: 100, Cancelled: 0 };
    return map[normalizeStatus(status)] || 25;
  };

  // Urgency values coming from the DB can be: morning / afternoon / evening /
  // emergency / critical / urgent / "—" / empty. The CSS only had classes for
  // "Emergency" and "Normal", so every other value rendered with no color
  // (invisible text). This maps every raw value to one of 4 known labels
  // that DO have matching CSS classes.
  const normalizeUrgency = (urgency) => {
    const map = {
      emergency: "Emergency",
      critical:  "Emergency",
      urgent:    "Emergency",
      morning:   "Morning",
      afternoon: "Afternoon",
      evening:   "Evening",
    };
    return map[(urgency || "").toLowerCase()] || "Normal";
  };

  // ========================= STATS =========================
  const totalRequests     = myListings.length;
  const pendingRequests   = myListings.filter((l) => normalizeStatus(l.status) === "Pending").length;
  const completedRequests = myListings.filter((l) => normalizeStatus(l.status) === "Received").length;
  const emergencyRequests = myListings.filter((l) =>
    ["emergency", "critical", "urgent"].includes((l.urgency || "").toLowerCase())
  ).length;

  // ========================= ACTIONS =========================
  const handleEdit            = (l) => { setSelectedListing(l); setShowEditModal(true); };
  const handleDelete          = (l) => { setSelectedListing(l); setShowDeleteModal(true); };
  const handleProofOfDelivery = (l) => { setSelectedListing(l); setShowProofModal(true); };
  const handleFeedback        = (l) => { setSelectedListing(l); setShowFeedbackModal(true); };
  const handleChat            = (l) => alert(`Opening chat for: ${l.itemName}`);
  const confirmDelete         = () => { alert(`Request ${selectedListing.id} cancelled`); setShowDeleteModal(false); };
  const submitFeedback        = () => { alert(`Feedback submitted! Rating: ${rating} stars`); setShowFeedbackModal(false); setRating(0); };
  const verifyOTP             = () => {
    if (otpCode === "123456") { alert("Delivery verified!"); setShowProofModal(false); setOtpCode(""); }
    else alert("Invalid OTP. Please try again.");
  };

  return (
    <ReceiverMasterLayout>
      <div className="listings-dashboard-wrapper">

        {showNotification && (
          <Alert variant="success" dismissible onClose={() => setShowNotification(false)} className="notification-alert-bar mb-3">
            <CheckCircle size={20} className="me-2" />
            <strong>Refreshed!</strong> All Food, Blood &amp; Medicine listings reloaded.
          </Alert>
        )}

        {/* Banner */}
        <div className="listings-page-banner">
          <div className="banner-content-area">
            <h1 className="banner-title-text">My Listings</h1>
            <p className="banner-subtitle-text">All your Food, Blood &amp; Medicine requests in one place</p>
          </div>
          <div className="banner-export-buttons">
            <button className="export-button-style" onClick={() => alert("Exporting PDF...")}>
              <Download size={18} /> Export PDF
            </button>
            <button className="export-button-style" onClick={() => alert("Exporting Excel...")}>
              <FileText size={18} /> Export Excel
            </button>
          </div>
        </div>

        {/* Stats */}
        <Row className="overview-stats-section mb-4">
          {[
            { label: "Total Requests",  value: totalRequests,     emoji: "📋", cls: "stat-card-blue"   },
            { label: "Pending",         value: pendingRequests,   emoji: "⏳", cls: "stat-card-yellow" },
            { label: "Completed",       value: completedRequests, emoji: "✅", cls: "stat-card-green"  },
            { label: "Emergency",       value: emergencyRequests, emoji: "🚨", cls: "stat-card-red"    },
          ].map((s, i) => (
            <Col lg={3} md={6} className="mb-3" key={i}>
              <Card className={`overview-stat-card ${s.cls}`}>
                <Card.Body>
                  <div className="stat-icon-display">{s.emoji}</div>
                  <div className="stat-data-info">
                    <h3 className="stat-value-number">{s.value}</h3>
                    <p className="stat-name-label">{s.label}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filters */}
        <Card className="filters-section-card mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={4} md={6} className="mb-3 mb-lg-0">
                <InputGroup>
                  <InputGroup.Text><Search size={18} /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, ID or city..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col lg={3} md={6} className="mb-3 mb-lg-0">
                <Form.Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="all">All Categories</option>
                  <option value="Food">🍽️ Food</option>
                  <option value="Blood">🩸 Blood</option>
                  <option value="Medicine">💊 Medicine</option>
                </Form.Select>
              </Col>
              <Col lg={3} md={6} className="mb-3 mb-lg-0">
                <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="shipped">Shipped</option>
                  <option value="received">Received</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
              <Col lg={2} md={6}>
                <button
                  className="notification-trigger-btn w-100"
                  onClick={() => { fetchListings(); setShowNotification(true); }}
                >
                  <Shield size={18} /> Refresh All
                </button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="listings-main-table-card">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted">Loading all listings...</p>
              </div>
            ) : (
              <div className="table-responsive-wrapper">
                <Table hover className="listings-data-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Item / Details</th>
                      <th>Quantity</th>
                      <th>City</th>
                      <th>Contact</th>
                      <th>Pickup Date</th>
                      <th>Urgency</th>
                      <th>Status &amp; Progress</th>
                      <th>Extra Info</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredListings.length > 0 ? (
                      filteredListings.map((listing, index) => {
                        const ns = normalizeStatus(listing.status);
                        const nu = normalizeUrgency(listing.urgency);
                        return (
                          <tr key={`${listing.category}-${listing.id}-${index}`} className="listing-table-row">

                            <td className="request-id-cell">
                              <small className="text-muted">{index + 1}</small>
                            </td>

                            <td className="category-cell">
                              <Badge
                                bg={getCategoryColor(listing.category)}
                                className="d-flex align-items-center gap-1"
                                style={{ width: "fit-content" }}
                              >
                                {getCategoryIcon(listing.category)} {listing.category}
                              </Badge>
                            </td>

                            <td className="item-details-cell">
                              <span className="item-name-title d-block fw-semibold">{listing.itemName}</span>
                              {listing.isFresh && (
                                <Badge bg="success" className="mt-1" style={{ fontSize: "10px" }}>
                                  {listing.category === "Food" ? "Fresh" : "Sealed"}
                                </Badge>
                              )}
                              {listing.category === "Blood" && listing.hospital !== "—" && (
                                <small className="text-muted d-block mt-1">🏥 {listing.hospital}</small>
                              )}
                              {listing.category === "Blood" && listing.relation !== "—" && (
                                <small className="text-muted d-block">👤 {listing.relation}</small>
                              )}
                            </td>

                            <td>
                              <span className="fw-semibold">{listing.quantity || "—"}</span>
                            </td>

                            <td>{listing.city}</td>

                            <td><small>{listing.contact}</small></td>

                            <td><small>{listing.pickupDate}</small></td>

                            <td className="urgency-cell">
                              <span className={`urgency-badge-display urgency-${nu}`}>
                                {nu}
                              </span>
                            </td>

                            <td className="status-tracking-cell" style={{ minWidth: "140px" }}>
                              <div className="status-info-wrapper">
                                <Badge
                                  bg={getStatusVariant(listing.status)}
                                  className="d-flex align-items-center gap-1 mb-1"
                                  style={{ width: "fit-content" }}
                                >
                                  {getStatusIcon(listing.status)} {ns}
                                </Badge>
                                <ProgressBar
                                  now={getProgress(listing.status)}
                                  variant={getStatusVariant(listing.status)}
                                  style={{ height: "5px" }}
                                />
                              </div>
                            </td>

                            <td>
                              <small className="text-muted d-block">{listing.extraInfo}</small>
                              {listing.expiryDate !== "—" && (
                                <small className="text-danger d-block">Exp: {listing.expiryDate}</small>
                              )}
                            </td>

                            <td className="actions-buttons-cell">
                              <div className="action-buttons-container">
                                <button
                                  className="action-btn-item btn btn-outline-primary btn-sm"
                                  onClick={() => handleEdit(listing)}
                                  disabled={ns === "Received" || ns === "Cancelled"}
                                  title="Edit"
                                ><Edit2 size={14} /></button>

                                <button
                                  className="action-btn-item btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(listing)}
                                  disabled={ns === "Received" || ns === "Cancelled"}
                                  title="Cancel"
                                ><Trash2 size={14} /></button>

                                <button
                                  className="action-btn-item btn btn-outline-success btn-sm"
                                  onClick={() => handleChat(listing)}
                                  title="Chat"
                                ><MessageCircle size={14} /></button>

                                {ns === "Received" && (
                                  <>
                                    <button
                                      className="action-btn-item btn btn-outline-info btn-sm"
                                      onClick={() => handleProofOfDelivery(listing)}
                                      title="Verify OTP"
                                    ><QrCode size={14} /></button>
                                    <button
                                      className="action-btn-item btn btn-outline-warning btn-sm"
                                      onClick={() => handleFeedback(listing)}
                                      title="Feedback"
                                    ><Star size={14} /></button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="11">
                          <div className="empty-listings-state text-center py-5">
                            <Package size={48} className="mb-3 text-muted" />
                            <h4>No Listings Found</h4>
                            <p className="text-muted">Try adjusting your filters or submit a new request</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Edit Request</Modal.Title></Modal.Header>
          <Modal.Body>
            {selectedListing && (
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Item Name</Form.Label>
                  <Form.Control type="text" defaultValue={selectedListing.itemName} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control type="text" defaultValue={selectedListing.quantity} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control type="text" defaultValue={selectedListing.city} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Contact</Form.Label>
                  <Form.Control type="text" defaultValue={selectedListing.contact} />
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Close</Button>
            <Button variant="primary" onClick={() => { alert("Updated!"); setShowEditModal(false); }}>Save Changes</Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Cancel Request</Modal.Title></Modal.Header>
          <Modal.Body>
            Are you sure you want to cancel <strong>{selectedListing?.itemName}</strong>?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>No</Button>
            <Button variant="danger" onClick={confirmDelete}>Yes, Cancel</Button>
          </Modal.Footer>
        </Modal>

        {/* Proof Modal */}
        <Modal show={showProofModal} onHide={() => setShowProofModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Verify Delivery</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <QrCode size={64} className="text-primary" />
              <p className="mt-3">Enter OTP to confirm delivery</p>
            </div>
            <Form.Group>
              <Form.Label>OTP Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                maxLength={6}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProofModal(false)}>Close</Button>
            <Button variant="success" onClick={verifyOTP}>Verify</Button>
          </Modal.Footer>
        </Modal>

        {/* Feedback Modal */}
        <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} centered>
          <Modal.Header closeButton><Modal.Title>Provide Feedback</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <p>Rate your experience for: <strong>{selectedListing?.itemName}</strong></p>
              <div className="rating-stars-container">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={32}
                    onClick={() => setRating(star)}
                    fill={rating >= star ? "#fbbf24" : "none"}
                    style={{ cursor: "pointer", color: rating >= star ? "#fbbf24" : "#ccc" }}
                  />
                ))}
              </div>
            </div>
            <Form.Group>
              <Form.Label>Comments (Optional)</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="Share your experience..." />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowFeedbackModal(false)}>Close</Button>
            <Button variant="primary" onClick={submitFeedback}>Submit Feedback</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </ReceiverMasterLayout>
  );
}

export default MyListings;